// 批量生成分镜 / 批量生视频（脚本节点开发方案 §8-4 定稿）：
// - 批量生成分镜 = 按镜头批量创建 IMAGE_GENERATOR 子节点（prompt = 资产图锚定区块
//   + 单镜提示词，未生成单镜提示词的行以该行画面描述兜底；锚定区块列出该镜提及且
//   已有资产图的资产「@资产名」引用）并自动排布、框选新节点；
// - 批量生视频 = 对已生成的分镜图批量创建 VIDEO_GENERATOR（图生视频，仅对已出图的
//   分镜行创建、未出图的跳过并 toast 提示）；
// - v1.4.96 视频节点同时连线分镜图（图生视频首图）与脚本节点（提示词 @资产/@分镜
//   提及的解析来源，同分镜生图节点先例；脚本节点无 data.image 不影响首图选择），
//   运动提示词尾部追加「画面参考：@分镜N」显式引用输入图（@节点标题 经名字表
//   画布标题兜底渲染缩略图芯片）；
// - v1.4.97 视频节点资产图锚定（对标分镜生图节点 v1.4.85）：按分镜图节点标题
//   「分镜 N」反查脚本镜头，收集该镜提及且已出图的锚定资产，写入 data.scriptShotRef
//   （节点参考图预览按序展开）并在提示词头部拼「资产图锚定」区块（@图片N 编号与
//   assetIds 顺序一致）；生成请求经 referenceImages 独立通道携带锚定资产图
//   （videoGeneratorStrategy 注入，不与图生视频首帧 inputImages 混用）；
// - 均通过 inputs 连线 + prompt 直写交接，不引入新节点类型；
// - 创建即经 onNodeAction 逐个入队（useGenerationOrchestrator 模块级串行队列天然排队）；
// - 布局沿用详情页批量建分块先例（useDetailPageActions：竖排堆叠 + 整组垂直居中）。
import { AppNode, NodeStatus, NodeType } from '../../types';
import { useNodeStore } from '../../stores/nodeStore';
import { useSelectionStore } from '../../stores/selectionStore';
import {
  buildAssetAnchorBlock,
  collectShotAnchorAssets,
  readScript,
  ScriptAsset,
  ScriptShotGenRef,
  Shot,
} from '../../types/scriptNode';
import { toast } from 'sonner';
// v1.4.84 仅类型引入（组件文件零运行时依赖，避免循环引用）
import type { BatchImageOptions, BatchVideoOptions } from './BatchGenerateModals';

/** 批量编排依赖（onAddNode 与详情页 generateBlockImage 同构：负责撤销历史 + 默认值组装） */
export interface ScriptBatchOps {
  scriptNodeId: string;
  onNodeAction: (id: string, prompt?: string) => void;
  onAddNode?: (
    type: NodeType,
    position?: { x: number; y: number },
    initialData?: Record<string, unknown>,
  ) => string | undefined;
  /** v1.4.84 弹窗确认后仅创建勾选的镜头（缺省 = 全部 eligible） */
  shotIds?: string[];
  /** v1.4.84 弹窗确认后仅为勾选的分镜图节点创建视频（缺省 = 全部已出图） */
  nodeIds?: string[];
  /** v1.4.84 分镜批量生图确认参数（弹窗底栏下拉：模型/画质/分辨率/比例） */
  imageOptions?: BatchImageOptions;
  /** v1.4.84 批量生视频确认参数（底栏模型/Audio/分辨率 + 行内时长与运动提示词，key = 分镜图节点 id） */
  videoOptions?: BatchVideoOptions;
}

// 布局参数（画布像素，对齐 useDetailPageActions 先例）
const STACK_PER_COLUMN = 4;
const STACK_GAP_Y = 60;
const STACK_GAP_X = 200;
const NODE_SHORT_EDGE = 420;
const X_OFFSET = 1000;
/** 分镜图节点行高估算（16:9 出图 + 卡片 UI 的实测均值兜底） */
const STORYBOARD_ROW_HEIGHT = 480;
const SCRIPT_HEIGHT_FALLBACK = 420;

/** 可参与批量生成分镜的镜头：单镜提示词就绪，或至少有画面描述兜底（§8-4） */
export function isStoryboardEligibleShot(shot: Shot): boolean {
  return (
    (shot.promptStatus === 'SUCCESS' && shot.prompt.trim().length > 0) ||
    shot.description.trim().length > 0
  );
}

/** 脚本节点的分镜图子节点（IMAGE_GENERATOR 且 inputs 含脚本节点；nodes 顺序即创建顺序） */
export function findStoryboardChildren(nodes: AppNode[], scriptNodeId: string): AppNode[] {
  return nodes.filter(
    (n) => n.type === NodeType.IMAGE_GENERATOR && n.inputs.includes(scriptNodeId),
  );
}

/** onAddNode 缺省兜底：直接组装节点入 store（对齐 handleAddNode 的默认值，不记主画布撤销历史） */
function fallbackAddNode(
  type: NodeType,
  position?: { x: number; y: number },
  initialData?: Record<string, unknown>,
): string {
  const defaults: Record<string, unknown> =
    type === NodeType.IMAGE_GENERATOR
      ? { model: 'grsai-gpt-image-2', aspectRatio: '16:9' }
      : type === NodeType.VIDEO_GENERATOR
        ? { model: 'grsai-minimax-h3', aspectRatio: '16:9', generationMode: 'DEFAULT' }
        : {};
  const node: AppNode = {
    id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    type,
    x: position?.x ?? 100,
    y: position?.y ?? 100,
    width: 420,
    title: '',
    status: NodeStatus.IDLE,
    data: { ...defaults, ...initialData } as AppNode['data'],
    inputs: [],
  };
  useNodeStore.getState().addNode(node);
  return node.id;
}

/** 批量生成分镜：按镜头批量创建 IMAGE_GENERATOR 子节点并自动排布、框选、逐个入队生成 */
export function generateBatchStoryboard({ scriptNodeId, onNodeAction, onAddNode, shotIds, imageOptions }: ScriptBatchOps): void {
  const store = useNodeStore.getState();
  const scriptNode = store.nodes.find((n) => n.id === scriptNodeId);
  // v1.4.26 修复：readScript 入参是 data.script，此前误传整个 node.data → 批量生视频静默失效
  const script = scriptNode ? readScript((scriptNode.data as { script?: unknown }).script) : undefined;
  if (!scriptNode || !script) return;

  // 防重：已存在分镜子节点时不重复堆积（重新生成分镜请先删除旧节点）
  if (findStoryboardChildren(store.nodes, scriptNodeId).length > 0) {
    toast.info('该脚本已生成分镜，请先删除旧分镜节点后重试');
    return;
  }

  const eligible = script.shots.filter(isStoryboardEligibleShot);
  if (eligible.length === 0) {
    toast.info('暂无可用的镜头提示词，请先在脚本面板完成「合成提示词」');
    return;
  }

  // v1.4.84 弹窗勾选过滤：仅创建确认面板中勾选的镜头（空数组视为未勾选，兜底全量）
  const selectedShots =
    shotIds && shotIds.length > 0 ? eligible.filter((s) => shotIds.includes(s.id)) : eligible;
  if (selectedShots.length === 0) {
    toast.info('请在确认面板中勾选需要生成的镜头');
    return;
  }

  // 自动排布：竖排堆叠（满 STACK_PER_COLUMN 开新列），整组垂直居中于脚本节点
  const rowCount = Math.min(selectedShots.length, STACK_PER_COLUMN);
  const groupHeight = rowCount * (STORYBOARD_ROW_HEIGHT + STACK_GAP_Y) - STACK_GAP_Y;
  const scriptHeight = scriptNode.height ?? SCRIPT_HEIGHT_FALLBACK;
  const groupTop = scriptNode.y + scriptHeight / 2 - groupHeight / 2;

  const addNodeAt = onAddNode ?? fallbackAddNode;
  // v1.4.35 画面描述兜底的行注入全局风格（单镜提示词已由 useScriptPrompts 生成时携带）
  const globalStyle = script.style.trim();
  const fallbackPrompt = (desc: string) =>
    globalStyle ? `${desc}。全局画面风格：${globalStyle}。` : desc;
  // v1.4.84 弹窗底栏参数写入节点 data（imageGeneratorStrategy 直读 model/aspectRatio/resolution；
  // quality 经 data.quality 透传 params.quality，常规画布节点无此字段不受影响）
  const optionData: Record<string, unknown> = imageOptions
    ? {
        model: imageOptions.modelId,
        aspectRatio: imageOptions.aspectRatio,
        resolution: imageOptions.resolution,
        ...(imageOptions.quality ? { quality: imageOptions.quality } : {}),
      }
    : {};
  const newIds: string[] = [];
  selectedShots.forEach((shot, i) => {
    const col = Math.floor(i / STACK_PER_COLUMN);
    const row = i % STACK_PER_COLUMN;
    // v1.4.85 资产图锚定：匹配该镜文本（描述/台词/提示词）提及且有资产图的资产，
    // 顺序即「@图片N」编号；prompt 头部拼锚定区块（资产图锚定+提示词），scriptShotRef
    // 存资产 id 顺序表供 imageGeneratorStrategy 运行时经脚本节点解析 imageUrl 注入参考图
    const anchorAssets = collectShotAnchorAssets(shot, script.assets);
    const anchorBlock = buildAssetAnchorBlock(anchorAssets);
    const shotRef: ScriptShotGenRef | undefined =
      anchorAssets.length > 0
        ? { scriptNodeId, shotId: shot.id, assetIds: anchorAssets.map((a) => a.id) }
        : undefined;
    const newId = addNodeAt(
      NodeType.IMAGE_GENERATOR,
      {
        x: scriptNode.x + X_OFFSET + col * (NODE_SHORT_EDGE + STACK_GAP_X),
        y: groupTop + row * (STORYBOARD_ROW_HEIGHT + STACK_GAP_Y),
      },
      {
        // prompt 直写交接：资产图锚定区块 + 单镜提示词优先，画面描述兜底（§8-4）
        prompt: `${anchorBlock}${shot.prompt.trim() || fallbackPrompt(shot.description.trim())}`,
        ...optionData,
        // v1.4.85 无锚定资产时不写 scriptShotRef（imageGeneratorStrategy 纯文生图不受影响）
        ...(shotRef ? { scriptShotRef: shotRef } : {}),
      },
    );
    if (!newId) return;
    newIds.push(newId);
    // addConnection 会把 TXT2IMG 翻转为 IMG2IMG：连线脚本节点为意图标注，实际请求按
    // 图片输入自动走文生图（imageGeneratorStrategy 运行时判定，同详情页分块先例）
    store.addConnection(scriptNodeId, newId);
    store.updateNode(newId, {}, undefined, `分镜 ${shot.index}`);
    onNodeAction(newId);
  });

  if (newIds.length > 0) {
    // 框选新节点（selectionStore 程序化框选标准入口）
    useSelectionStore.getState().setSelectedNodeIds(newIds);
    toast.success(`已创建 ${newIds.length} 个分镜节点，开始逐个生成`);
  }
}

/** 批量生视频：对已出图的分镜批量创建 VIDEO_GENERATOR（图生视频），未出图的跳过并提示 */
export function generateBatchVideos({ scriptNodeId, onNodeAction, onAddNode, nodeIds, videoOptions }: ScriptBatchOps): void {
  const store = useNodeStore.getState();
  const scriptNode = store.nodes.find((n) => n.id === scriptNodeId);
  // v1.4.97 补读脚本数据（同 generateBatchStoryboard 先例）：供分镜图节点标题反查镜头、
  // 收集锚定资产；脚本数据损坏时 script 为 undefined，锚定静默跳过，生视频主流程不受影响
  const script = scriptNode ? readScript((scriptNode.data as { script?: unknown }).script) : undefined;
  if (!scriptNode) return;

  const children = findStoryboardChildren(store.nodes, scriptNodeId);
  if (children.length === 0) {
    toast.info('暂无分镜节点，请先「批量生成分镜」');
    return;
  }

  const withImage = children.filter((n) => !!(n.data as { image?: string }).image);
  if (withImage.length === 0) {
    toast.info('暂无已出图的分镜，请等待分镜图生成完成');
    return;
  }

  // v1.4.84 弹窗勾选过滤：仅为确认面板中勾选的分镜图节点创建视频（空数组兜底全量）
  const selectedChildren =
    nodeIds && nodeIds.length > 0 ? withImage.filter((n) => nodeIds.includes(n.id)) : withImage;
  if (selectedChildren.length === 0) {
    toast.info('请在确认面板中勾选需要生成视频的镜头');
    return;
  }
  const skipped = withImage.length - selectedChildren.length;

  const addNodeAt = onAddNode ?? fallbackAddNode;
  // v1.4.84 弹窗参数写入 VIDEO 节点 data：videoGeneratorStrategy 直读
  // model/resolution/duration(秒)/generateAudio；prompts[child.id] 为该镜头运动提示词（prompt 直写交接）
  // v1.4.96 运动提示词尾部追加「画面参考：@分镜N」：分镜图已作为图生视频输入（inputs 连线），
  // 提示词同步以 @节点标题 显式引用该图（名字表按画布节点标题兜底可渲染缩略图芯片）；
  // 无标题/无弹窗参数时保持原语义（不写冗余字段）
  const buildOptionData = (child: AppNode): Record<string, unknown> => {
    const title = child.title?.trim();
    const imageRef = title ? `\n画面参考：@${title}` : '';
    // v1.4.97 资产图锚定（对标分镜生图节点 v1.4.85）：标题「分镜 N」反查脚本镜头
    // （同 ScriptNodeToolbar videoItems 先例正则；匹配失败不按序号兜底——锚定必须
    // 精确，错镜头的资产会污染视频语义，宁缺勿错），collectShotAnchorAssets 收集
    // 该镜提及且已出图的资产（顺序即 scriptShotRef.assetIds 注入顺序），prompt 头部拼锚定区块 +
    // scriptShotRef 存资产 id 顺序表：节点参考图预览按序展开（useInputAssets 自动
    // 生效），生成请求由 videoGeneratorStrategy 经 referenceImages 独立通道注入
    let anchorAssets: ScriptAsset[] = [];
    let shotRef: ScriptShotGenRef | undefined;
    if (script && title) {
      const m = /^分镜\s*(\d+)$/.exec(title);
      const shot = m ? script.shots.find((s) => s.index === parseInt(m[1], 10)) : undefined;
      if (shot) {
        anchorAssets = collectShotAnchorAssets(shot, script.assets);
        if (anchorAssets.length > 0) {
          shotRef = { scriptNodeId, shotId: shot.id, assetIds: anchorAssets.map((a) => a.id) };
        }
      }
    }
    const anchorBlock = buildAssetAnchorBlock(anchorAssets);
    if (!videoOptions) {
      const prompt = `${anchorBlock}${imageRef}`.trim();
      if (!prompt && !shotRef) return {};
      return { ...(prompt ? { prompt } : {}), ...(shotRef ? { scriptShotRef: shotRef } : {}) };
    }
    const motionPrompt = videoOptions.prompts?.[child.id]?.trim() ?? '';
    return {
      model: videoOptions.modelId,
      resolution: videoOptions.resolution,
      ...(videoOptions.durations?.[child.id] ? { duration: videoOptions.durations[child.id] } : {}),
      generateAudio: videoOptions.generateAudio,
      ...(motionPrompt || imageRef || anchorBlock ? { prompt: `${anchorBlock}${motionPrompt}${imageRef}` } : {}),
      ...(shotRef ? { scriptShotRef: shotRef } : {}),
    };
  };
  const newIds: string[] = [];
  selectedChildren.forEach((child) => {
    const newId = addNodeAt(
      NodeType.VIDEO_GENERATOR,
      {
        // 排布在对应分镜图正右侧（分镜组本身竖排错开，天然不重叠）
        x: child.x + NODE_SHORT_EDGE + STACK_GAP_X,
        y: child.y,
      },
      // 图生视频交接：inputs 连线分镜图节点，videoGeneratorStrategy 按图片输入自动走 image-to-video
      buildOptionData(child),
    );
    if (!newId) return;
    newIds.push(newId);
    // 连线分镜图节点：图生视频首图来源（collectInputImages 读 data.image）
    store.addConnection(child.id, newId);
    // v1.4.96 补连线脚本节点：视频提示词中 @资产/@分镜 提及的解析来源（同分镜生图节点
    // 先例）；脚本节点无 data.image 不会混入图生视频输入，addConnection 的模式翻转也
    // 仅针对 IMAGE_GENERATOR，视频节点不受影响
    store.addConnection(scriptNodeId, newId);
    store.updateNode(newId, {}, undefined, child.title ? `${child.title} 视频` : undefined);
    onNodeAction(newId);
  });

  if (newIds.length > 0) {
    useSelectionStore.getState().setSelectedNodeIds(newIds);
    if (skipped > 0) toast.info(`${skipped} 个分镜未出图，已跳过`);
    toast.success(`已创建 ${newIds.length} 个视频节点，开始逐个生成`);
  }
}
